import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Edit3, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]); // [{name,label,is_builtin}]
  const [open, setOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/roles").then((r) => setRoles(r.data));
  };
  useEffect(() => { load(); }, []);

  const isAdmin = me?.role === "admin";

  const removeUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        eyebrow="Access Control"
        title="User Management"
        subtitle={isAdmin
          ? "Create accounts for sales executives, technicians, and customers. Define roles and set initial passwords."
          : "Only admins can manage users. You're viewing in read-only mode."}
        action={
          isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setRolesOpen(true)} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-11 px-4" data-testid="manage-roles-button">
                <Shield className="w-4 h-4 mr-2" /> Manage Roles
              </Button>
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
                <DialogTrigger asChild>
                  <Button onClick={() => setEditing(null)} className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-user-button">
                    <Plus className="w-4 h-4 mr-2" /> New User
                  </Button>
                </DialogTrigger>
                <UserDialog
                  key={editing?.id || "new"}
                  editing={editing}
                  roles={roles}
                  onDone={() => { setOpen(false); setEditing(null); load(); }}
                />
              </Dialog>
              <ManageRolesDialog
                open={rolesOpen}
                onOpenChange={setRolesOpen}
                roles={roles}
                onChange={load}
              />
            </div>
          )
        }
      />

      <div className="p-4 sm:p-8">
        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                {["Name", "Email", "Role", "Created", ""].map((h) => (
                  <th key={h} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-semibold">{u.name}</td>
                  <td className="px-4 py-3 text-xs font-mono">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold bg-[#F3F4F6] text-[#0a0a0a] px-2 py-0.5">{u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <div className="flex gap-1 justify-end">
                        <Button
                          onClick={() => { setEditing(u); setOpen(true); }}
                          variant="ghost"
                          size="sm"
                          className="rounded-none h-7 text-[10px] uppercase tracking-widest font-bold"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Edit3 className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        {u.id !== me.id && (
                          <Button
                            onClick={() => removeUser(u.id)}
                            variant="ghost"
                            size="sm"
                            className="rounded-none h-7 text-[10px] uppercase tracking-widest font-bold text-[#EF4444] hover:text-[#EF4444]"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="text-center text-sm text-[#9CA3AF] py-12">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UserDialog({ editing, roles, onDone }) {
  const roleList = roles && roles.length > 0 ? roles : [
    { name: "admin", label: "Admin" }, { name: "sales", label: "Sales" },
    { name: "technician", label: "Technician" }, { name: "customer", label: "Customer" },
  ];
  const [form, setForm] = useState({
    email: editing?.email || "",
    name: editing?.name || "",
    role: editing?.role || roleList[0]?.name || "sales",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });

  const submit = async () => {
    if (!editing && (!form.email || !form.password)) {
      toast.error("Email and password required");
      return;
    }
    if (!form.name) {
      toast.error("Name required");
      return;
    }
    setLoading(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role, email: form.email };
        if (form.password.trim()) body.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, body);
        toast.success("User updated");
      } else {
        await api.post(`/admin/users`, form);
        toast.success("User created");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-md">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">
          {editing ? "Edit User" : "Create User"}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={set("email")}
          testid="user-email"
        />
        <Field label="Full Name" value={form.name} onChange={set("name")} testid="user-name" />
        <div>
          <Label className="label-eyebrow">Role</Label>
          <Select value={form.role} onValueChange={set("role")}>
            <SelectTrigger className="rounded-none mt-2 h-10" data-testid="user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {roleList.map((r) => <SelectItem key={r.name} value={r.name}>{r.label || r.name}{!r.is_builtin && " · custom"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Field
          label={editing ? "New Password (leave blank to keep)" : "Initial Password"}
          type="password"
          value={form.password}
          onChange={set("password")}
          testid="user-password"
        />
        <Button onClick={submit} disabled={loading} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="user-submit">
          {loading ? "Saving…" : editing ? "Update User" : "Create User"}
        </Button>
      </div>
    </DialogContent>
  );
}

function Field({ label, value, onChange, type = "text", disabled, testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="rounded-none mt-2 h-10 disabled:bg-[#F3F4F6]"
        data-testid={testid}
      />
    </div>
  );
}

function ManageRolesDialog({ open, onOpenChange, roles, onChange }) {
  const [newName, setNewName] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!newName.trim()) { toast.error("Role name required"); return; }
    setBusy(true);
    try {
      await api.post("/roles", {
        name: newName.trim().toLowerCase(),
        label: newLabel.trim() || undefined,
        description: newDesc.trim() || undefined,
      });
      toast.success(`Role "${newName}" created`);
      setNewName(""); setNewLabel(""); setNewDesc("");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete role "${name}"? Users with this role must be reassigned first.`)) return;
    try {
      await api.delete(`/roles/${name}`);
      toast.success("Role deleted");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="manage-roles-dialog">
        <DialogHeader>
          <DialogTitle className="font-black text-2xl tracking-tight">Manage Roles</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="border border-[#E5E7EB] p-4 space-y-3">
            <div className="label-eyebrow">Existing Roles</div>
            <div className="space-y-1">
              {roles.map((r) => (
                <div key={r.name} className="flex items-center justify-between gap-2 border-b border-[#F3F4F6] py-2" data-testid={`role-row-${r.name}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">
                      {r.label || r.name}
                      {r.is_builtin && <span className="ml-2 text-[9px] uppercase tracking-widest font-bold text-[#9CA3AF]">built-in</span>}
                    </div>
                    <div className="text-[11px] text-[#4B5563] truncate">{r.description || (r.is_builtin ? "Core role — cannot be removed" : "Custom role")}</div>
                  </div>
                  {!r.is_builtin && (
                    <button onClick={() => remove(r.name)} className="text-[10px] uppercase tracking-widest font-bold text-[#DC2626] hover:underline" data-testid={`role-delete-${r.name}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="border border-[#0a0a0a] p-4 space-y-3">
            <div className="label-eyebrow">Add New Role</div>
            <Field label="Role Key (lowercase, no spaces)" value={newName} onChange={(e) => setNewName(e.target.value)} testid="new-role-name" />
            <Field label="Display Label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} testid="new-role-label" />
            <Field label="Description (optional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} testid="new-role-description" />
            <Button onClick={create} disabled={busy} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="new-role-submit">
              {busy ? "Creating…" : "Create Role"}
            </Button>
            <div className="text-[10px] text-[#4B5563]">
              New roles start with only <b>Dashboard</b> access. Open the <b>Permissions</b> page to expand the menus they can see.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

